import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { galleryProjects } from "@/lib/galleryData";
import { caseStudyContent } from "@/lib/caseStudyData";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const VP_80 = { once: true, margin: "-80px" };
const VP_40 = { once: true, margin: "-40px" };
const HERO_INIT = { opacity: 0, y: 40 };
const HERO_ANIM = { opacity: 1, y: 0 };
const HERO_T = { duration: 1, ease: [0.22, 1, 0.36, 1] };

export default function CaseStudy() {
  const { slug } = useParams();
  const { t, lang, setLang } = useLanguage();

  const index = galleryProjects.findIndex((p) => p.key === slug);
  const project = index !== -1 ? galleryProjects[index] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (project) document.title = `${project.title} — MEDERNACH LIONEL DESIGNER`;
    return () => {
      document.title = "MEDERNACH LIONEL DESIGNER | Studio de design intérieur & extérieur — Luxembourg";
    };
  }, [slug, project]);

  if (!project) return <Navigate to="/" replace />;

  const content = (caseStudyContent[slug] && caseStudyContent[slug][lang]) || caseStudyContent[slug].fr;
  const prev = galleryProjects[(index - 1 + galleryProjects.length) % galleryProjects.length];
  const next = galleryProjects[(index + 1) % galleryProjects.length];

  return (
    <div data-testid="case-study-page" className="bg-[#0A1420] text-white min-h-screen">
      {/* Slim header */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#0A1420]/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="case-home-link"
            className="font-serif-lux text-lg tracking-[0.25em] uppercase text-[#A0ABC0] hover:text-[#D4AF37] transition-colors duration-300"
          >
            HOME
          </Link>
          <div className="flex items-center gap-4">
            <a
              href="/#consultation"
              data-testid="case-header-cta"
              className="hidden sm:inline-block border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1420] transition-colors duration-300 px-4 py-2.5 uppercase tracking-[0.12em] text-[11px]"
            >
              {t("caseStudy.ctaButton")}
            </a>
            <div
              data-testid="case-lang-toggle"
              className="flex items-center text-xs tracking-widest uppercase border border-white/15 px-2.5 py-1.5"
            >
              {["fr", "en", "lb"].map((l, i) => (
                <span key={l} className="flex items-center">
                  {i > 0 && <span className="mx-1.5 opacity-30">/</span>}
                  <button
                    onClick={() => setLang(l)}
                    data-testid={`case-lang-${l}`}
                    className={`transition-colors duration-300 ${
                      lang === l ? "text-[#D4AF37]" : "text-[#A0ABC0] hover:text-white"
                    }`}
                  >
                    {l.toUpperCase()}
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[72vh] min-h-[520px] flex items-end overflow-hidden">
        <img src={project.cover} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1420] via-[#0A1420]/55 to-[#0A1420]/40" />
        <motion.div
          initial={HERO_INIT}
          animate={HERO_ANIM}
          transition={HERO_T}
          className="relative max-w-[1600px] mx-auto w-full px-6 sm:px-10 pb-16"
        >
          <nav className="flex items-center gap-2 text-xs text-[#A0ABC0] uppercase tracking-[0.2em] mb-6">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">
              {t("caseStudy.breadcrumbHome")}
            </Link>
            <span className="opacity-40">/</span>
            <a href="/#gallery" className="hover:text-[#D4AF37] transition-colors">
              {t("caseStudy.breadcrumbProjects")}
            </a>
            <span className="opacity-40">/</span>
            <span className="text-[#D4AF37]">{project.title}</span>
          </nav>
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.25em] mb-4">
            {t(`gallery.categories.${project.categoryKey}`)}
          </p>
          <h1 className="font-serif-lux text-5xl sm:text-6xl lg:text-7xl leading-none text-white">
            {project.title}
          </h1>
          <p className="mt-6 font-serif-lux italic text-2xl sm:text-3xl gold-gradient-text max-w-2xl">
            {content.tagline}
          </p>
        </motion.div>
      </section>

      {/* Narrative */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={VP_80}
            className="lg:col-span-6"
          >
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">
              {t("caseStudy.contextTitle")}
            </p>
            <p className="text-[#A0ABC0] text-lg font-light leading-relaxed">{content.context}</p>
          </motion.div>
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={VP_80}
            className="lg:col-span-6"
          >
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">
              {t("caseStudy.visionTitle")}
            </p>
            <p className="text-[#A0ABC0] text-lg font-light leading-relaxed">{content.vision}</p>
          </motion.div>
        </div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={VP_80}
          className="mt-20 max-w-3xl"
        >
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">
            {t("caseStudy.approachTitle")}
          </p>
          <p className="font-serif-lux text-2xl sm:text-3xl leading-snug text-white">{content.approach}</p>
        </motion.div>

        <div className="mt-16">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">
            {t("caseStudy.materialsTitle")}
          </p>
          <div className="flex flex-wrap gap-3">
            {content.materials.map((m) => (
              <span
                key={m}
                className="border border-[#D4AF37]/30 text-[#A0ABC0] px-5 py-2 text-sm tracking-wide"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Before / After */}
      {project.beforeAfter && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-8">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-8">
            {t("caseStudy.beforeAfterTitle")}
          </p>
          <BeforeAfter
            before={project.beforeAfter.before}
            after={project.beforeAfter.after}
            beforeLabel={t("caseStudy.beforeLabel")}
            afterLabel={t("caseStudy.afterLabel")}
            fit={project.beforeAfter.fit}
          />
        </section>
      )}

      {/* Gallery */}
      <section className="bg-[#0B1622] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-12">
            {t("caseStudy.galleryTitle")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {project.photos.map((src, idx) => (
              <motion.div
                key={src}
                initial={HERO_INIT}
                whileInView={HERO_ANIM}
                viewport={VP_40}
                transition={{ duration: 0.7, delay: (idx % 2) * 0.08 }}
                data-testid={`case-photo-${idx}`}
                className="group overflow-hidden border border-[#D4AF37]/10 bg-[#080f18] flex items-center justify-center"
              >
                <img
                  src={src}
                  alt={`${project.title} — ${idx + 1}`}
                  className="w-full h-96 object-contain transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Result + CTA */}
      <section className="max-w-4xl mx-auto px-6 sm:px-8 py-24 sm:py-32 text-center">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">
          {t("caseStudy.resultTitle")}
        </p>
        <p className="font-serif-lux text-3xl sm:text-4xl leading-tight text-white">{content.result}</p>

        <div className="mt-16 border-t border-[#D4AF37]/20 pt-16">
          <h2 className="font-serif-lux text-3xl sm:text-4xl text-white">{t("caseStudy.ctaTitle")}</h2>
          <p className="mt-5 text-[#A0ABC0] font-light max-w-xl mx-auto">{t("caseStudy.ctaText")}</p>
          <a
            href="/#consultation"
            data-testid="case-footer-cta"
            className="inline-block mt-10 bg-[#D4AF37] text-[#0A1420] font-semibold hover:bg-[#E6C27A] transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-sm"
          >
            {t("caseStudy.ctaButton")}
          </a>
        </div>
      </section>

      {/* Prev / Next */}
      <nav className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-24 grid grid-cols-2 gap-4">
        <Link
          to={`/projet/${prev.key}`}
          data-testid="case-prev"
          className="group border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 transition-colors duration-300 p-6 sm:p-8"
        >
          <span className="flex items-center gap-2 text-xs text-[#A0ABC0] uppercase tracking-widest">
            <ArrowLeft size={16} className="text-[#D4AF37]" /> {t("caseStudy.prev")}
          </span>
          <p className="mt-3 font-serif-lux text-2xl text-white group-hover:text-[#D4AF37] transition-colors">
            {prev.title}
          </p>
        </Link>
        <Link
          to={`/projet/${next.key}`}
          data-testid="case-next"
          className="group border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 transition-colors duration-300 p-6 sm:p-8 text-right"
        >
          <span className="flex items-center justify-end gap-2 text-xs text-[#A0ABC0] uppercase tracking-widest">
            {t("caseStudy.next")} <ArrowRight size={16} className="text-[#D4AF37]" />
          </span>
          <p className="mt-3 font-serif-lux text-2xl text-white group-hover:text-[#D4AF37] transition-colors">
            {next.title}
          </p>
        </Link>
      </nav>

      <Footer />
    </div>
  );
}
